import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../Router/constant";
import {
  BellIcon,
  ChevronDownIcon,
  LogOutIcon,
  MenuIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
} from "./Icons";
import "./Header.css";

const ACCOUNT_LINKS = [
  {
    key: "users",
    label: "User Management",
    hint: "Users, roles and permissions",
    icon: UsersIcon,
    to: RoutesPaths.USER_MANAGEMENT,
  },
  {
    key: "settings",
    label: "Settings",
    hint: "System preferences and defaults",
    icon: SettingsIcon,
    to: RoutesPaths.SETTINGS,
  },
];

export default function Header({ onToggleSidebar, notificationCount = 8 }) {
  const searchRef = useRef(null);
  const accountRef = useRef(null);
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);

  // "/" focuses search, as the placeholder hint advertises.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "/") return;
      const tag = event.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close the account menu on an outside click or Escape.
  useEffect(() => {
    if (!accountOpen) return undefined;

    const handlePointer = (event) => {
      if (!accountRef.current?.contains(event.target)) setAccountOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setAccountOpen(false);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [accountOpen]);

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar__icon-btn"
        onClick={onToggleSidebar}
        aria-label="Toggle navigation"
      >
        <MenuIcon size={22} />
      </button>

      <div className="topbar__search d-none d-sm-flex">
        <SearchIcon size={18} />
        <input
          ref={searchRef}
          type="search"
          placeholder="Search taxpayers, NIF, name, or reference..."
          aria-label="Search taxpayers"
        />
        <kbd className="topbar__kbd">/</kbd>
      </div>

      {/* The full search field does not fit a phone width. */}
      <button
        type="button"
        className="topbar__icon-btn d-sm-none"
        aria-label="Search taxpayers"
      >
        <SearchIcon size={21} />
      </button>

      <div className="topbar__actions">
        <button
          type="button"
          className="topbar__icon-btn topbar__icon-btn--badged"
          aria-label={`Notifications (${notificationCount} unread)`}
        >
          <BellIcon size={21} />
          {notificationCount > 0 && (
            <span className="topbar__badge">{notificationCount}</span>
          )}
        </button>

        <button
          type="button"
          className="topbar__icon-btn"
          aria-label="Security status"
        >
          <ShieldCheckIcon size={21} />
        </button>

        <span className="topbar__divider" />

        <div className="topbar__account-wrap" ref={accountRef}>
          <button
            type="button"
            className="topbar__account"
            aria-expanded={accountOpen}
            aria-haspopup="menu"
            onClick={() => setAccountOpen((prev) => !prev)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/DGI-logo.png`}
              alt=""
              className="topbar__account-logo"
            />
            <span className="topbar__account-meta">
              <span className="topbar__account-name">DGI Angola</span>
              <span className="topbar__account-org">
                Direção Geral dos Impostos
              </span>
            </span>
            <ChevronDownIcon size={17} />
          </button>

          {accountOpen && (
            <div className="acctmenu" role="menu">
              <div className="acctmenu__head">
                <span className="acctmenu__avatar">
                  <UserIcon size={20} />
                </span>
                <span className="acctmenu__meta">
                  <span className="acctmenu__name">John Doe</span>
                  <span className="acctmenu__role">Super Administrator</span>
                </span>
              </div>

              <div className="acctmenu__list">
                {ACCOUNT_LINKS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      className="acctmenu__item"
                      role="menuitem"
                      onClick={() => setAccountOpen(false)}
                    >
                      <span className="acctmenu__icon">
                        <Icon size={17} />
                      </span>
                      <span>
                        <span className="acctmenu__label">{item.label}</span>
                        <span className="acctmenu__hint">{item.hint}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>

              <button
                type="button"
                className="acctmenu__item acctmenu__item--signout"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  navigate(RoutesPaths.LOGIN);
                }}
              >
                <span className="acctmenu__icon">
                  <LogOutIcon size={17} />
                </span>
                <span className="acctmenu__label">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
